import { twMerge } from "tailwind-merge";
import { formatFieldValue } from "../../../utils/formatting/formatFieldValue";
import type { FieldFormat } from "../../../utils/formatting/types";
import { Icon, type IconName } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import type { CellComponentProps, CellEmphasis } from "../DataTable.types";
import { getCellContainerClasses, getSkeletonSize, resolveValue } from "./cellUtils";

/** Which way the value moved. Derived from the sign unless stated explicitly. */
export type DeltaDirection = "up" | "down" | "flat";

/**
 * Whether the move is good news.
 *
 * **Independent of {@link DeltaDirection} on purpose.** A fall in consumption is
 * direction `down`, tone `positive`; a rise in error rate is direction `up`, tone
 * `negative`. Collapsing the two into one signed value is the modelling mistake this
 * cell exists to avoid — `Kpi` expresses the same idea per-component with
 * `deltaIntent: "upIsGood" | "downIsGood"`, which cannot vary by row.
 */
export type DeltaTone = "positive" | "negative" | "neutral";

export type DeltaFormat = "number" | "percent" | "currency" | "compact";

export interface DeltaCellProps<T = any> extends CellComponentProps<T> {
  /**
   * The change itself, signed. For `format: "percent"` this is **percentage points** —
   * `68`, not `0.68` — matching `NumberCell`'s percent format and the `percent` cell.
   */
  value: number | string | null | undefined;

  /**
   * Which arrow to draw. Omitted, it follows the sign of `value`, so the common case
   * cannot contradict itself. Pass it explicitly only when magnitude and direction
   * genuinely disagree.
   */
  direction?: DeltaDirection | ((row: T) => DeltaDirection);

  /** Which colour to use. See {@link DeltaTone} — deliberately not tied to `direction`. */
  tone?: DeltaTone | ((row: T) => DeltaTone);

  /** How to render the magnitude. Routed through the shared formatting system. */
  format?: DeltaFormat;

  /** Fraction digits, 0–20. Applied as both the minimum and the maximum. */
  decimals?: number;

  /** ISO 4217 code for `format: "currency"`. */
  currency?: string;

  /** Set `false` for a bare signed number with no arrow. */
  showIcon?: boolean;

  align?: "left" | "center" | "right";
  emphasis?: CellEmphasis;

  /** Shown when the value is null, undefined or not a finite number. */
  emptyText?: string;

  className?: string;
}

/**
 * `Arrow*`, not `Trend*`.
 *
 * `Trend*` reads as a series; a period-over-period delta is a single step, which is
 * what an arrow means. `Kpi` — older and far more used — already draws arrows, so this
 * is also the spelling that leaves the design system with one answer instead of two.
 */
const DIRECTION_ICON: Record<DeltaDirection, IconName> = {
  up: "ArrowUp",
  down: "ArrowDown",
  flat: "Minus",
};

/**
 * The `-text` half of the feedback triple.
 *
 * `--color-feedback-success` does not exist — the tokens are
 * `--color-feedback-success-{text,background,border}` — so the bare
 * `text-feedback-success` spelling some components carried resolved to nothing.
 */
const TONE_CLASS: Record<DeltaTone, string> = {
  positive: "text-feedback-success-text",
  negative: "text-feedback-error-text",
  neutral: "text-text-secondary",
};

/** The default `direction`: whatever the sign says. */
export function deltaDirectionFromValue(value: number): DeltaDirection {
  if (value > 0) return "up";
  if (value < 0) return "down";
  return "flat";
}

/**
 * The cell's four formats, expressed as the shared `FieldFormat` union.
 *
 * There is no private `Intl` copy here: `formatFieldValue` is the same path a `Kpi`, a
 * `StatList` and a `formatted` cell take, so a delta and the number it is a delta of
 * cannot disagree about grouping or precision.
 */
function toFieldFormat(format: DeltaFormat, decimals: number, currency: string): FieldFormat {
  switch (format) {
    case "percent":
      return { type: "percentage", decimals };
    case "currency":
      return {
        type: "currency",
        currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      };
    case "compact":
      return { type: "number", notation: "compact", decimals, thousandsSeparator: true };
    default:
      return { type: "number", decimals, thousandsSeparator: true };
  }
}

/**
 * DeltaCell
 *
 * A period-over-period change: an arrow, a signed magnitude, and a tone that says
 * whether the move is good news.
 *
 * Only positive values get an explicit `+`; a negative sign comes from the formatter
 * and zero gets no sign at all.
 */
export function DeltaCell<T = any>({
  value,
  row,
  context,
  direction,
  tone = "neutral",
  format = "number",
  decimals = 0,
  currency = "USD",
  showIcon = true,
  align = "right",
  emphasis = "normal",
  emptyText = "—",
  className,
}: DeltaCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  const num = typeof value === "number" ? value : Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(num)) {
    return <div className={getCellContainerClasses(align, "text-text-secondary")}>{emptyText}</div>;
  }

  const resolvedDirection = resolveValue(direction, row) ?? deltaDirectionFromValue(num);
  const resolvedTone = resolveValue(tone, row) ?? "neutral";

  const formatted = formatFieldValue(num, toFieldFormat(format, decimals, currency)) ?? emptyText;
  const sign = num > 0 ? "+" : "";

  // Emphasis contributes weight only. Colour on this cell is the tone: emphasis's own
  // `text-text-primary` would otherwise win the `text-*` slot through `twMerge` and
  // silently repaint a positive delta as body text.
  //
  // `TONE_CLASS` goes LAST for the same reason. `twMerge` resolves a conflict in favour
  // of the later class, so with the tone before `className` any caller passing a
  // `text-*` utility — for spacing, for a font size, for anything — would take the
  // colour with it. The tone is the cell's meaning, not a default to be overridden;
  // a caller who wants a different colour wants a different `tone`.
  const weightClass = emphasis === "strong" || emphasis === "high" ? "font-semibold" : undefined;
  const containerClasses = getCellContainerClasses(align, twMerge(weightClass, className, TONE_CLASS[resolvedTone]));

  return (
    <div className={containerClasses}>
      {showIcon && <Icon name={DIRECTION_ICON[resolvedDirection]} size="xs" aria-hidden />}
      <span>{`${sign}${formatted}`}</span>
    </div>
  );
}
